<template>
  <div class="overlay">
    <div class="modal">
      <div class="modal-header">
        <h3>📧 Aperçu de la convocation</h3>
        <button class="close-btn" @click="close">×</button>
      </div>

      <div v-if="loading" class="modal-body">
        <p>Chargement de l'aperçu...</p>
      </div>

        <div v-else class="modal-body">
        <div v-if="!readOnly && emailTo" class="mail-preview-header">
          <div class="mail-preview-row">
            <span class="mail-preview-label">Objet</span>
            <span class="mail-preview-subject">Convocation à un entretien individuel 📅</span>
          </div>
          <div class="mail-preview-row">
            <span class="mail-preview-label">À</span>
            <div class="mail-recipient-badge">
              <span class="mail-recipient-icon"></span>
              <span class="mail-recipient-address">{{ emailTo }}</span>
            </div>
          </div>
          <div v-if="emailCopy.length" class="mail-preview-row">
            <span class="mail-preview-label">Cc</span>
            <div class="mail-copy-list">
              <span v-for="(copy, index) in emailCopy" :key="index" class="mail-copy-chip">{{ copy }}</span>
            </div>
          </div>
        </div>
        <div v-if="!readOnly" class="editor-header">
          <button
              :class="['btn', 'btn-light', 'btn-sm', 'edit-toggle-btn', { active: isEditMode }]"
              :title="isEditMode ? 'Désactiver l’édition' : 'Activer l’édition'"
              type="button"
              @click="toggleEditMode"
          >
            <span class="edit-toggle-icon" aria-hidden="true">✎</span>
            <span class="edit-toggle-label">Éditer la convocation</span>
          </button>
        </div>
        <div v-if="!readOnly && isEditMode" class="editor-toolbar" @mousedown.prevent>
          <button class="btn btn-light btn-sm tool-btn" title="Gras" type="button" @click="applyCommand('bold')"><strong>B</strong></button>
          <button class="btn btn-light btn-sm tool-btn" title="Italique" type="button" @click="applyCommand('italic')"><em>I</em></button>
          <button class="btn btn-light btn-sm tool-btn" title="Souligné" type="button" @click="applyCommand('underline')"><u>U</u></button>
          <button class="btn btn-light btn-sm tool-btn" title="Liste à puces" type="button" @click="applyCommand('insertUnorderedList')">•</button>
          <button class="btn btn-light btn-sm tool-btn" title="Liste numérotée" type="button" @click="applyCommand('insertOrderedList')">1.</button>
          <button class="btn btn-light btn-sm tool-btn align-icon align-left" title="Aligner à gauche" type="button" @click="applyCommand('justifyLeft')"></button>
          <button class="btn btn-light btn-sm tool-btn align-icon align-center" title="Centrer" type="button" @click="applyCommand('justifyCenter')"></button>
          <button class="btn btn-light btn-sm tool-btn align-icon align-right" title="Aligner à droite" type="button" @click="applyCommand('justifyRight')"></button>
          <button class="btn btn-light btn-sm tool-btn" title="Ajouter ou modifier un lien" type="button" @click="manageLink">🔗</button>
          <button
              class="btn btn-light btn-sm rollback-btn tool-btn"
              title="Réinitialiser le contenu"
              type="button"
              @click="restoreInitialHtml"
          >↺
          </button>
        </div>
        <p v-if="!readOnly && linkErrorMessage" class="editor-error">{{ linkErrorMessage }}</p>
        <iframe
            v-if="previewHtml"
            :key="frameRenderKey"
            ref="previewFrame"
            :srcdoc="frameSrcdoc"
            class="preview-frame"
            @load="onFrameLoad"
        ></iframe>
      </div>

      <div  class="modal-footer">
        <button class="btn btn-secondary" @click="close">Annuler</button>
        <button
            :disabled="sending"
            class="btn btn-primary"
            @click="confirmSend"
        >
          {{ sending ? 'Envoi...' : readOnly ? 'Renvoyer la convocation' : 'Envoyer la convocation' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import {onMounted, onUnmounted, ref} from 'vue'
import {usePlanningStore} from '@/store/planningStore'

const props = defineProps({
  ofObjId: String | Number,
  userAccountObjId: String | Number,
  meeting: Object,
  readOnly: {
    type: Boolean,
    default: false
  }
})
const emits = defineEmits(['close', 'sent'])

const planningStore = usePlanningStore()
const EDITABLE_SELECTOR = 'span[data-param="body"][data-index="1"]'
const loading = ref(true)
const sending = ref(false)
const previewHtml = ref('')
const frameSrcdoc = ref('')
const frameRenderKey = ref(0)
const editedHtml = ref('')
const isEditMode = ref(false)
const emailTo = ref('')
const emailCopy = ref([])
const linkErrorMessage = ref('')
const previewFrame = ref(null)
let frameInputHandler = null
let frameSelectionHandler = null
let savedSelectionRange = null
const readOnly = props.readOnly

onMounted(() => {
  fetchPreview()
})

onUnmounted(() => {
  detachFrameListeners()
})

const fetchPreview = () => {
  planningStore.fetchConvocationPreview(props.ofObjId, props.userAccountObjId, props.meeting.id).then((data) => {
    previewHtml.value = data.preview
    frameSrcdoc.value = data.preview
    editedHtml.value = data.preview
    emailTo.value = data.emailTo
    emailCopy.value = data.emailCopy || []
  }).catch((err) => {
    console.error('Erreur lors du chargement de la preview', err)
  }).finally(() => {
    loading.value = false
  })
}

const getFrameDocument = () => {
  return previewFrame.value?.contentDocument || previewFrame.value?.contentWindow?.document || null
}

const getEditableTargets = () => {
  const doc = getFrameDocument()
  if (!doc) return []
  return Array.from(doc.querySelectorAll(EDITABLE_SELECTOR))
}

const asElement = (node) => {
  if (!node) return null
  if (node.nodeType === Node.ELEMENT_NODE) return node
  if (node.nodeType === Node.TEXT_NODE) return node.parentElement
  return null
}

const isEditableTargetOrInside = (element) => {
  const candidate = asElement(element)
  if (!candidate || typeof candidate.closest !== 'function') return false
  return !!candidate.closest(EDITABLE_SELECTOR)
}

const applyFocusStyles = () => {
  const targets = getEditableTargets()
  if (targets.length === 0) return

  targets.forEach((target) => {
    if (isEditMode.value) {
      target.style.outline = '2px dashed #1f6feb'
      target.style.outlineOffset = '2px'
      target.style.backgroundColor = 'rgba(31, 111, 235, 0.08)'
      target.style.borderRadius = '4px'
      target.style.padding = '1px 2px'
    } else {
      target.style.outline = ''
      target.style.outlineOffset = ''
      target.style.backgroundColor = ''
      target.style.borderRadius = ''
      target.style.padding = ''
    }
  })
}

const setEditableState = () => {
  const targets = getEditableTargets()
  targets.forEach((target) => {
    const doc = getFrameDocument()
    if (doc && !target.dataset.codexOriginalDisplay) {
      const computedDisplay = doc.defaultView?.getComputedStyle(target).display
      target.dataset.codexOriginalDisplay = computedDisplay || ''
    }

    target.contentEditable = isEditMode.value ? 'true' : 'false'
    target.spellcheck = false
    target.style.cursor = isEditMode.value ? 'text' : 'default'
    if (isEditMode.value) {
      if (target.dataset.codexOriginalDisplay === 'contents') {
        target.style.display = 'inline-block'
      }
    } else {
      target.style.display = ''
    }

    if (isEditMode.value) {
      target.setAttribute('tabindex', '0')
    } else {
      target.removeAttribute('tabindex')
    }
  })
}

const saveSelectionRange = () => {
  const doc = getFrameDocument()
  if (!doc) return
  const selection = doc.getSelection()
  if (!selection || selection.rangeCount === 0) return
  const range = selection.getRangeAt(0)
  const node = asElement(range.commonAncestorContainer)
  if (!node) return
  if (!isEditableTargetOrInside(node)) return
  savedSelectionRange = range.cloneRange()
}

const restoreSelectionRange = () => {
  const doc = getFrameDocument()
  if (!doc || !savedSelectionRange) return
  const selection = doc.getSelection()
  if (!selection) return
  selection.removeAllRanges()
  selection.addRange(savedSelectionRange)
}

const isSelectionInsideEditableTarget = () => {
  const doc = getFrameDocument()
  if (!doc) return false
  const selection = doc.getSelection()
  if (!selection || selection.rangeCount === 0) return false
  const anchor = asElement(selection.anchorNode)
  const focus = asElement(selection.focusNode)
  if (!anchor || !focus) return false
  return isEditableTargetOrInside(anchor) && isEditableTargetOrInside(focus)
}

const ensureSelectionInEditableTarget = () => {
  if (isSelectionInsideEditableTarget()) return true
  restoreSelectionRange()
  return isSelectionInsideEditableTarget()
}

const detachFrameListeners = () => {
  const targets = getEditableTargets()
  const doc = getFrameDocument()
  if (!frameInputHandler) return
  targets.forEach((target) => {
    target.removeEventListener('input', frameInputHandler)
    target.removeEventListener('keyup', frameInputHandler)
    target.removeEventListener('mouseup', frameInputHandler)
    target.removeEventListener('focus', frameInputHandler)
  })
  frameInputHandler = null

  if (doc && frameSelectionHandler) {
    doc.removeEventListener('selectionchange', frameSelectionHandler)
    frameSelectionHandler = null
  }
}

const syncEditedHtmlFromFrame = () => {
  const doc = getFrameDocument()
  if (!doc?.documentElement) return
  editedHtml.value = doc.documentElement.outerHTML
}

const getConvocationEmailBody = () => {
  const doc = getFrameDocument()
  const editableTarget = doc?.querySelector(EDITABLE_SELECTOR)
  if (editableTarget) {
    return editableTarget.innerHTML
  }

  if (editedHtml.value) {
    const parsedDoc = new DOMParser().parseFromString(editedHtml.value, 'text/html')
    return parsedDoc.querySelector(EDITABLE_SELECTOR)?.outerHTML || ''
  }

  return ''
}

const onFrameLoad = () => {
  const doc = getFrameDocument()
  if (!doc) return

  detachFrameListeners()
  applyFocusStyles()
  setEditableState()

  frameInputHandler = () => {
    syncEditedHtmlFromFrame()
    saveSelectionRange()
  }
  frameSelectionHandler = () => {
    saveSelectionRange()
  }

  getEditableTargets().forEach((target) => {
    target.addEventListener('input', frameInputHandler)
    target.addEventListener('keyup', frameInputHandler)
    target.addEventListener('mouseup', frameInputHandler)
    target.addEventListener('focus', frameInputHandler)
  })
  doc.addEventListener('selectionchange', frameSelectionHandler)
}

const applyCommand = (command, value = null) => {
  if (!isEditMode.value) return
  const doc = getFrameDocument()
  if (!doc) return

  previewFrame.value?.contentWindow?.focus()
  if (!isSelectionInsideEditableTarget()) restoreSelectionRange()
  if (!ensureSelectionInEditableTarget()) return

  doc.execCommand(command, false, value)
  syncEditedHtmlFromFrame()
  saveSelectionRange()
  previewFrame.value?.contentWindow?.focus()
}

const getSelectedAnchor = () => {
  const doc = getFrameDocument()
  if (!doc) return null
  const selection = doc.getSelection()
  if (!selection || selection.rangeCount === 0) return null

  const range = selection.getRangeAt(0)
  const node = asElement(range.commonAncestorContainer)
  if (!node || typeof node.closest !== 'function') return null
  return node.closest('a')
}

const getSelectionRangeInEditableTarget = () => {
  const doc = getFrameDocument()
  if (!doc) return null
  const selection = doc.getSelection()
  if (!selection || selection.rangeCount === 0) return null
  const range = selection.getRangeAt(0)
  const node = asElement(range.commonAncestorContainer)
  if (!node) return null
  if (!isEditableTargetOrInside(node)) return null
  return range
}

const unwrapAnchor = (anchor) => {
  const parent = anchor.parentNode
  if (!parent) return
  while (anchor.firstChild) {
    parent.insertBefore(anchor.firstChild, anchor)
  }
  parent.removeChild(anchor)
}

const normalizeLinkUrl = (url) => {
  const trimmedUrl = url.trim()
  if (!trimmedUrl) return trimmedUrl
  if (/^www\./i.test(trimmedUrl)) return `https://${trimmedUrl}`
  return trimmedUrl
}

const isValidLinkUrl = (url) => {
  try {
    const parsedUrl = new URL(url)
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(parsedUrl.protocol)
  } catch {
    return false
  }
}

const manageLink = () => {
  if (!isEditMode.value) return
  linkErrorMessage.value = ''
  previewFrame.value?.contentWindow?.focus()
  ensureSelectionInEditableTarget()
  const initialRange = getSelectionRangeInEditableTarget()
  if (!initialRange) return
  const selectedAnchor = getSelectedAnchor()
  const currentUrl = selectedAnchor?.getAttribute('href') || ''
  const url = window.prompt('URL du lien (laisser vide pour supprimer)', currentUrl)

  if (url === null) return

  const normalizedUrl = normalizeLinkUrl(url)
  previewFrame.value?.contentWindow?.focus()
  ensureSelectionInEditableTarget()

  const range = getSelectionRangeInEditableTarget()
  if (!range) return
  const anchorInSelection = getSelectedAnchor()

  if (normalizedUrl === '') {
    if (anchorInSelection) {
      unwrapAnchor(anchorInSelection)
      syncEditedHtmlFromFrame()
      saveSelectionRange()
      return
    }
    applyCommand('unlink')
    return
  }

  if (!isValidLinkUrl(normalizedUrl)) {
    linkErrorMessage.value = 'Lien invalide. Utilisez une URL valide (https://..., mailto:... ou tel:...).'
    return
  }

  if (anchorInSelection) {
    anchorInSelection.setAttribute('href', normalizedUrl)
    anchorInSelection.setAttribute('target', '_blank')
    anchorInSelection.setAttribute('rel', 'noopener noreferrer')
    syncEditedHtmlFromFrame()
    saveSelectionRange()
    return
  }

  if (range.collapsed) return

  const doc = getFrameDocument()
  if (!doc) return
  const anchor = doc.createElement('a')
  anchor.setAttribute('href', normalizedUrl)
  anchor.setAttribute('target', '_blank')
  anchor.setAttribute('rel', 'noopener noreferrer')

  try {
    range.surroundContents(anchor)
  } catch (err) {
    const content = range.extractContents()
    anchor.appendChild(content)
    range.insertNode(anchor)
  }

  syncEditedHtmlFromFrame()
  saveSelectionRange()
}

const restoreInitialHtml = () => {
  frameSrcdoc.value = previewHtml.value
  editedHtml.value = previewHtml.value
  frameRenderKey.value += 1
}

const toggleEditMode = () => {
  if (readOnly) return
  isEditMode.value = !isEditMode.value
  setEditableState()
  applyFocusStyles()
  if (isEditMode.value) {
    const [firstTarget] = getEditableTargets()
    firstTarget?.focus()
  }
}

const confirmSend = () => {
  sending.value = true
  const meetingData = {
    meetingStatut: readOnly ? 31 : 30,
    candidacyObjId: props.meeting.candidacyObjId,
    convocationEmailBody: getConvocationEmailBody(),
  }
  planningStore.updateMeeting(props.ofObjId, props.userAccountObjId, props.meeting.id, meetingData).then(() => {
    emits('sent')
  }).catch((err) => {
    console.error('Erreur lors de l’envoi de la convocation', err)
  }).finally(() => {
    sending.value = false
  })
}

const close = () => emits('close')

</script>

<style scoped>
/* Overlay similaire à MeetingEdit.vue */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
}

/* Fenêtre agrandie et centrée */
.modal {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  height: 90%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: fadeIn 0.25s ease;
  top: 1%
}

/* Header stylé */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--primary);
  color: var(--white);
  padding: 10px 20px;
  font-weight: 600;
  border-bottom: 1px solid #dce3ed;
}

.modal-header h3 {
  font-size: 18px;
  margin: 0;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: var(--white);
  transition: 0.2s;
}


/* Corps de la popup */
.modal-body {
  flex: 1;
  padding: 10px 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fafafa;
  padding: 6px;
}

.editor-header {
  display: flex;
  justify-content: flex-end;
}

.edit-toggle-btn {
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #d3deed;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffffff 0%, #f5f8fc 100%);
  color: #2d4f78;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.edit-toggle-btn:hover {
  border-color: #b9cae2;
  background: #eef4ff;
}

.edit-toggle-icon {
  font-size: 16px;
  transform: translateY(-0.5px);
}

.edit-toggle-label {
  white-space: nowrap;
}

.edit-toggle-btn.active {
  background: var(--primary);
  border-color: var(--primary);
  color: var(--white);
  box-shadow: 0 4px 10px rgba(31, 111, 235, 0.22);
}

.rollback-btn {
  font-size: 13px;
  line-height: 1;
}

.tool-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  font-size: 14px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.align-icon {
  background-repeat: no-repeat;
  background-color: transparent;
}

.align-left {
  background-image:
      linear-gradient(#444, #444),
      linear-gradient(#444, #444),
      linear-gradient(#444, #444),
      linear-gradient(#444, #444);
  background-size: 18px 2px, 12px 2px, 16px 2px, 10px 2px;
  background-position: 6px 8px, 6px 13px, 6px 18px, 6px 23px;
}

.align-center {
  background-image:
      linear-gradient(#444, #444),
      linear-gradient(#444, #444),
      linear-gradient(#444, #444),
      linear-gradient(#444, #444);
  background-size: 18px 2px, 12px 2px, 16px 2px, 10px 2px;
  background-position: 7px 8px, 10px 13px, 8px 18px, 11px 23px;
}

.align-right {
  background-image:
      linear-gradient(#444, #444),
      linear-gradient(#444, #444),
      linear-gradient(#444, #444),
      linear-gradient(#444, #444);
  background-size: 18px 2px, 12px 2px, 16px 2px, 10px 2px;
  background-position: 6px 8px, 12px 13px, 8px 18px, 14px 23px;
}

.editor-error {
  margin: -2px 0 0;
  color: #dc3545;
  font-size: 12px;
}

/* Frame plus haute */
.preview-frame {
  flex: 1;
  width: 100%;
  height: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
}

/* En-tete mail (zone destinataires) */
.mail-preview-header {
  margin-top: 6px;
  border: 1px solid #dce3ed;
  border-radius: 10px;
  background: linear-gradient(180deg, #fbfcfe 0%, #f4f7fb 100%);
  overflow: hidden;
}

.mail-preview-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
}

.mail-preview-row + .mail-preview-row {
  border-top: 1px solid #e7edf6;
}

.mail-preview-label {
  width: 34px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: #74839b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.mail-preview-subject {
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #2b3d55;
}

.mail-recipient-badge {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d4deeb;
  border-radius: 999px;
  background: #fff;
  padding: 4px 10px 4px 8px;
}

.mail-recipient-icon {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #dbe8ff;
  position: relative;
  flex-shrink: 0;
}

.mail-recipient-icon::after {
  content: '';
  position: absolute;
  top: 4px;
  left: 5px;
  width: 8px;
  height: 8px;
  border-right: 1.5px solid #2f69c8;
  border-bottom: 1.5px solid #2f69c8;
  transform: rotate(45deg);
}

.mail-recipient-address {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #2b3d55;
}

.mail-copy-list {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.mail-copy-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border: 1px solid #d4deeb;
  border-radius: 999px;
  background: #fff;
  color: #41556f;
  font-size: 12px;
  line-height: 1.2;
  padding: 4px 10px;
}

@media (max-width: 640px) {
  .mail-preview-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .mail-preview-label {
    width: auto;
  }
}

/* Footer fixe */
.modal-footer {
  display: flex !important;
  flex-direction: row !important;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-top: 1px solid #e2e6ea;
  background: #fafafa;
  gap: 20px; /* optionnel : espace entre les deux boutons */
}

.modal-footer .btn {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-width: 140px;
}


/* Animation légère */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
